import React, { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { Checkbox, Chip, ListItemText, ListSubheader, MenuItem, Select as MuiSelect, SelectChangeEvent } from '@mui/material'
import { FormikValues, useField, useFormikContext } from 'formik'
import { customSelectOption, customValueContainerWithPlaceholder } from '../../shared/common'
import IntervalInput from '../../shared/intervalInput'
import NestedDropdown, { NestedOption } from '../../shared/NestedDropdown'
import TooltipLabel from '../../shared/forms/TooltipLabel'
import { t } from '../../../i18n'
import { SelectOption, ServerErrors } from '../catalogueTypes'

const scope = 'product'
const ATTACHMENT_FIELD = 'attachment'
const NO_SEPARATOR = () => null

interface FormStatus {
  errors?: ServerErrors
}

const useServerError = (name: string): [string[], () => void] => {
  const { status, setStatus } = useFormikContext<FormikValues>()
  const messages: string[] = (status as FormStatus | undefined)?.errors?.[name] ?? []
  const clear = () => {
    if (messages.length === 0) return
    const { [name]: _cleared, ...rest } = (status as FormStatus).errors ?? {}
    setStatus({ errors: rest })
  }
  return [messages, clear]
}

const ErrorText = ({ messages }: { messages: string[] }) =>
  messages.length > 0 ? <span className="error-text">{messages.join(', ')}</span> : null

const errorStateClass = (messages: string[]) => (messages.length > 0 ? 'error-state' : '')

const withValidationError = (serverErrors: string[], touched: boolean, error?: string): string[] =>
  touched && error ? [...serverErrors, error] : serverErrors

export const CatalogueTextInput = ({ name, label }: { name: string; label: string }) => {
  const [field] = useField<string | null>(name)
  const [errors, clearError] = useServerError(name)

  return (
    <div className="form-input-wrapper">
      <div className="form-input">
        <span className="form-label">{label}</span>
        <input
          id={name}
          name={name}
          className="form-control"
          placeholder={label}
          value={field.value ?? ''}
          onChange={(event) => {
            field.onChange(event)
            clearError()
          }}
        />
      </div>
      <ErrorText messages={errors} />
    </div>
  )
}

export const CatalogueTextAreaInput = ({ name, label }: { name: string; label: string }) => {
  const [field] = useField<string | null>(name)
  const [errors, clearError] = useServerError(name)

  return (
    <div className="form-group">
      <label>{label}</label>
      <textarea
        name={name}
        value={field.value ?? ''}
        className="form-control"
        onChange={(event) => {
          field.onChange(event)
          clearError()
        }}
      />
      <ErrorText messages={errors} />
    </div>
  )
}

interface ImageValues extends FormikValues {
  attachment: File | null
  file_url: string | null
}

const ImagePreview = ({ url }: { url: string | null }) =>
  url ? (
    <>
      <img id="image" src={url} />
      <div className="overlay">
        <div>{t('catalogue.change_image')}</div>
      </div>
    </>
  ) : (
    <>
      <i className="bi bi-card-image"></i>
      <div className="overlay text-center">
        <div>{t('catalogue.add_image')}</div>
      </div>
    </>
  )

export const CatalogueImageAndNameInput = ({ placeholder }: { placeholder: string }) => {
  const { values, setFieldValue } = useFormikContext<ImageValues>()
  const [name] = useField<string>('name')
  const [nameErrors, clearNameError] = useServerError('name')
  const [attachmentErrors, clearAttachmentError] = useServerError(ATTACHMENT_FIELD)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    },
    [previewUrl]
  )

  return (
    <div className="input-wrapper">
      <div className="d-flex">
        <label htmlFor={ATTACHMENT_FIELD} className="file-placeholder">
          <ImagePreview url={previewUrl ?? values.file_url} />
        </label>
        <input
          className="file-input-hidden"
          id={ATTACHMENT_FIELD}
          type="file"
          accept="image/jpeg,image/png"
          name={ATTACHMENT_FIELD}
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null
            setFieldValue(ATTACHMENT_FIELD, file)
            setPreviewUrl(file ? URL.createObjectURL(file) : null)
            clearAttachmentError()
          }}
        />
        <div className="form-group w-100">
          <input
            id="name"
            name="name"
            onChange={(event) => {
              name.onChange(event)
              clearNameError()
            }}
            className={`form-control big-input ${errorStateClass(nameErrors)}`}
            placeholder={placeholder}
            value={name.value}
          />
          <ErrorText messages={nameErrors} />
        </div>
      </div>
      <div>
        <ErrorText messages={attachmentErrors} />
      </div>
    </div>
  )
}

interface CatalogueRadioGroupFrameProps {
  label: string
  labelClassName?: string
  errors: string[]
  children: React.ReactNode
  aside?: React.ReactNode
}

const CatalogueRadioGroupFrame = ({ label, labelClassName = '', errors, children, aside }: CatalogueRadioGroupFrameProps) => (
  <div className={`robis-radio-group-wrapper ${errorStateClass(errors)}`}>
    <div className="robis-radio-group">
      <span className={`robis-radio-group-label ${labelClassName}`}>{label}</span>
      <div className="robis-radio-group-input">
        <div>{children}</div>
      </div>
      {aside}
    </div>
    <ErrorText messages={errors} />
  </div>
)

interface CatalogueRadioGroupProps {
  name: string
  label: string
  options: ReadonlyArray<readonly [string, string]>
  onValueChange?: (value: string) => void
}

export const CatalogueRadioGroup = ({ name, label, options, onValueChange }: CatalogueRadioGroupProps) => {
  const [field, , helpers] = useField<string | null>(name)
  const [errors, clearError] = useServerError(name)

  return (
    <CatalogueRadioGroupFrame label={label} errors={errors}>
      {options.map(([value, text]) => (
        <label className={`radio-${value} me-4`} key={value}>
          <input
            type="radio"
            name={name}
            value={value}
            onChange={() => {
              helpers.setValue(value)
              clearError()
              onValueChange?.(value)
            }}
            checked={field.value === value}
          />
          {text}
        </label>
      ))}
    </CatalogueRadioGroupFrame>
  )
}

export const ProductModifierRadio = ({ name, children }: { name: string; children: React.ReactNode }) => {
  const [field, meta, helpers] = useField<boolean | null>(name)
  const [serverErrors, clearError] = useServerError(name)
  const errors = withValidationError(serverErrors, meta.touched, meta.error)

  const select = (value: boolean) => {
    helpers.setValue(value)
    clearError()
  }

  return (
    <CatalogueRadioGroupFrame label={t('is_modifier', { scope })} errors={errors} aside={<div className="mb-1">{children}</div>}>
      <label className="radio-is-modifier me-4">
        <input type="radio" name={name} value="true" onChange={() => select(true)} checked={field.value === true} />
        {t('product_is_modifier', { scope })}
      </label>
      <label className="radio-not-modifier">
        <input type="radio" name={name} value="false" onChange={() => select(false)} checked={field.value === false} />
        {t('product_is_not_modifier', { scope })}
      </label>
    </CatalogueRadioGroupFrame>
  )
}

interface CatalogueSelectFrameProps {
  label: string
  errors: string[]
  children: React.ReactNode
  className?: string
}

const CatalogueSelectFrame = ({ label, errors, children, className = '' }: CatalogueSelectFrameProps) => (
  <div className={`form-select-input-wrapper ${errorStateClass(errors)}`}>
    <div className={`form-select-input ${className}`.trim()}>
      <span>{label}</span>
      {children}
    </div>
    <ErrorText messages={errors} />
  </div>
)

interface CatalogueSelectProps {
  name: string
  label: string
  options: SelectOption[]
}

const SINGLE_SELECT_COMPONENTS = {
  Option: customSelectOption,
  ValueContainer: customValueContainerWithPlaceholder,
  IndicatorSeparator: NO_SEPARATOR
}

export const CatalogueSelect = ({ name, label, options }: CatalogueSelectProps) => {
  const [field, meta, helpers] = useField<SelectOption | null>(name)
  const [serverErrors, clearError] = useServerError(name)
  const errors = withValidationError(serverErrors, meta.touched, meta.error)

  return (
    <CatalogueSelectFrame label={label} errors={errors}>
      <Select<SelectOption>
        className="react-select mb-3"
        classNamePrefix="react-select"
        placeholder={label}
        options={options}
        value={field.value}
        isSearchable={false}
        onChange={(option) => {
          helpers.setValue(option)
          clearError()
        }}
        components={SINGLE_SELECT_COMPONENTS}
      />
    </CatalogueSelectFrame>
  )
}

export const CatalogueMultiSelect = ({ name, label, options }: CatalogueSelectProps) => {
  const [field, , helpers] = useField<SelectOption[]>(name)
  const [errors, clearError] = useServerError(name)
  const optionsById = useMemo(() => new Map(options.map((option) => [option.value, option])), [options])
  const selectedIds = useMemo(() => field.value.map((option) => option.value), [field.value])
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const applySelection = (selection: SelectOption[]) => {
    helpers.setValue(selection)
    clearError()
  }

  const handleChange = (event: SelectChangeEvent<number[]>) => {
    const nextIds = new Set(event.target.value as number[])
    applySelection(options.filter((option) => nextIds.has(option.value)))
  }

  return (
    <CatalogueSelectFrame label={label} errors={errors}>
      <MuiSelect<number[]>
        className="react-select catalogue-mui-select mb-3"
        multiple
        value={selectedIds}
        onChange={handleChange}
        MenuProps={{ PaperProps: { style: { maxHeight: '50vh' } } }}
        renderValue={(ids) => (
          <div className="catalogue-multiselect-value">
            {ids.map((id) => {
              const option = optionsById.get(id)
              return option ? <Chip key={id} label={option.label} size="small" /> : null
            })}
          </div>
        )}
      >
        <ListSubheader className="multiple-menu-header" onClick={(event) => event.stopPropagation()}>
          <span className="me-2 as-button" onClick={() => applySelection(options)}>
            Select all
          </span>
          <span className="as-button" onClick={() => applySelection([])}>
            Clear all
          </span>
        </ListSubheader>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={selectedIdSet.has(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </MuiSelect>
    </CatalogueSelectFrame>
  )
}

interface CategoryPickerProps {
  name: string
  label: string
  options: NestedOption[]
}

export const CategoryPicker = ({ name, label, options }: CategoryPickerProps) => {
  const [field, meta, helpers] = useField<SelectOption | null>(name)
  const [serverErrors, clearError] = useServerError(name)
  const errors = withValidationError(serverErrors, meta.touched, meta.error)

  return (
    <CatalogueSelectFrame label={label} errors={errors} className="label-left">
      <NestedDropdown
        options={options}
        value={field.value}
        handleChange={(option) => {
          helpers.setValue(option ? { label: option.label, value: Number(option.value) } : null)
          clearError()
        }}
        placeholder={label}
        className="long-label"
      />
    </CatalogueSelectFrame>
  )
}

interface CatalogueStepperProps {
  name: string
  label: string
  unit?: string
  tooltip?: string
  maxLength?: number
  step?: number
  decimals?: number
}

// null is a valid, meaningful value here -- it means "cleared", as opposed to 0.
const isNonNegativeOrCleared = (value: string | number | null): boolean =>
  value === null || (Number.isFinite(Number(value)) && Number(value) >= 0)

export const CatalogueStepper = ({ name, label, unit = '', tooltip = '', maxLength, step, decimals }: CatalogueStepperProps) => {
  const [field, meta, helpers] = useField<string | number | null>(name)
  const [serverErrors, clearError] = useServerError(name)
  const errors = withValidationError(serverErrors, meta.touched, meta.error)

  const handleChange = (_fieldName: string, value: string | number | null) => {
    if (isNonNegativeOrCleared(value)) {
      helpers.setValue(value)
      clearError()
    }
  }

  return (
    <IntervalInput
      mandatory={false}
      label={<TooltipLabel text={label} tooltip={tooltip} />}
      name={name}
      value={field.value}
      onChange={handleChange}
      afterText={unit}
      tooltipText={tooltip}
      maxLength={maxLength}
      step={step}
      decimals={decimals}
      classNames="narrow long-label"
      error={errors.join(', ')}
    />
  )
}
